/**
 * State lozenge: color + icon + i18n label, all driven by the shared state
 * map. A distinct icon per category means color is never the only carrier
 * of meaning (WCAG 1.4.1). Colors come from the theme's semantic palette.
 */
import { Chip, type ChipProps } from "@mui/material";
import ScheduleIcon from "@mui/icons-material/Schedule";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CancelIcon from "@mui/icons-material/Cancel";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useTranslation } from "react-i18next";
import { categoryOf, type StateCategory } from "../../lib/states";

const CATEGORY_COLOR: Record<StateCategory, ChipProps["color"]> = {
  queued: "default",
  running: "info",
  waiting: "warning",
  success: "success",
  warning: "warning",
  failure: "error",
};

const CATEGORY_ICON: Record<StateCategory, React.ReactElement> = {
  queued: <ScheduleIcon />,
  running: <AutorenewIcon />,
  waiting: <HourglassTopIcon />,
  success: <CheckCircleIcon />,
  warning: <WarningAmberIcon />,
  failure: <CancelIcon />,
};

export function StateChip({
  state,
  fallbackLabel,
  sx,
}: {
  /** Raw backend state name, e.g. "RUNNING". */
  state: string;
  /** Shown when no i18n label exists for the state (e.g. dashboard_status). */
  fallbackLabel?: string;
  sx?: ChipProps["sx"];
}) {
  const { t } = useTranslation();
  const category = categoryOf(state);
  return (
    <Chip
      size="small"
      color={category ? CATEGORY_COLOR[category] : "default"}
      icon={category ? CATEGORY_ICON[category] : <HelpOutlineIcon />}
      label={t(`states.${state}`, fallbackLabel ?? state)}
      sx={sx}
    />
  );
}
